import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "./App.css";
import MainRoutes from "./routes/MainRoutes";
import { toast } from "react-toastify";
import Header from "./layout/Header.js";
import Tabs from "./components/Layouts/Tabs.jsx";

import Table_0 from "./components/Tables/Table_0.jsx";
import Table_1 from "./components/Tables/Table_1.jsx";
import Table_2 from "./components/Tables/Table_2.jsx";
import Table_3 from "./components/Tables/Table_3.jsx";
import CustomModal from "./components/Modals/CustomModal.jsx";
import OutboundNoticeModal from "./components/Modals/OutboundNoticeModal.jsx";
import OrderPrint from "./components/Print/OrderPrint.jsx";

import InfoCard from "./components/Layouts/InfoCard.jsx";
import PassWordInitialize from "./components/Layouts/PassWordInitialize.jsx";
import SessionExpiryModal from "./components/Modals/SessionExpiryModal.jsx";
import { useAuth } from "./context/AuthContext";
import InfoCardStaffOnly from "./components/Layouts/InfoCardStaffOnly.jsx";
import { FaBars } from "react-icons/fa";

import {
  getClientInfo,
  getOrderBook,
  getClientItemList,
  getOutboundNoticeList,
  getSupplyTypeCode,
  addOrderBook,
  addOrderItem,
  showOrderItemList,
  updateOrderItem,
  deleteOrderBook,
  deleteOrderItem,
  getOIserial,
  updateClientRequest,
  updateFieldTable,
  fetchUpdatedRow,
  sendChatMessage,
} from "./models/Query";
import { isOrderInProgress } from "./utils/orderStatus";

// 재사용 가능한 유틸 함수들 (컴포넌트 외부로 분리하여 불필요한 재생성 방지)
const getSafeProductIndex = (list, productCode) => {
  if (!Array.isArray(list) || !productCode) return -1;

  return list.findIndex(
    (item) =>
      item &&
      typeof item === "object" &&
      "ProductCode" in item &&
      item.ProductCode === productCode,
  );
};

const getTargetPageFromIndex = (index, itemsPerPage = 8) => {
  if (typeof index !== "number" || index < 0) return null;
  return Math.floor(index / itemsPerPage) + 1;
};

const createOrderMessage = (clientName, orderItemList) => {
  const suplyFlagMap = {
    1: "",
    2: "반품",
  };
  const getQuantityUnitLabel = (packingUnit) =>
    Number(packingUnit || 1) === 1 ? "Ea" : "Box";

  let message = `■주문처 : ${clientName}\n`;
  message += "---------------------------------------------\n";
  message += "Item (Qty)     \n";
  message += "---------------------------------------------\n";

  orderItemList.forEach((item) => {
    const name = item.ModelName.slice(0, 25);
    const qty = Number(item.Qty).toLocaleString();
    const unitLabel = getQuantityUnitLabel(item.PackingUnit);
    const firstDigit = String(item.SuplyFlag).charAt(0);
    const flag = suplyFlagMap[firstDigit] || "";
    message += `${name}   (${qty}${unitLabel})    ${flag}\n`;
  });

  return "\n" + message + "\n\n\n\n";
};

const createModifiedOrderMessage = (clientName, originalItems, updatedItems) => {
  const itemByProductCode = (items) =>
    new Map(items.map((item) => [String(item.ProductCode), item]));
  const updatedItemsByCode = itemByProductCode(updatedItems);
  const originalItemsByCode = itemByProductCode(originalItems);
  const formatItemName = (item) => item.ModelName || item.ItemName || "";
  const formatQuantity = (item) => {
    const quantity = Number(item.Qty);
    const formattedQuantity = Number.isFinite(quantity)
      ? quantity.toLocaleString()
      : String(item.Qty);
    const unit = Number(item.PackingUnit || 1) === 1 ? "Ea" : "Box";
    return `${formattedQuantity}${unit}`;
  };
  const changes = [];

  originalItems.forEach((originalItem) => {
    const updatedItem = updatedItemsByCode.get(
      String(originalItem.ProductCode),
    );
    if (!updatedItem) {
      changes.push(
        `(주문취소) ${formatItemName(originalItem)} (${formatQuantity(originalItem)})`,
      );
    } else if (Number(originalItem.Qty) !== Number(updatedItem.Qty)) {
      changes.push(
        `(수량수정) ${formatItemName(updatedItem)} (${formatQuantity(originalItem)}) → (${formatQuantity(updatedItem)})`,
      );
    }
  });

  updatedItems.forEach((updatedItem) => {
    if (!originalItemsByCode.has(String(updatedItem.ProductCode))) {
      changes.push(
        `(주문추가) ${formatItemName(updatedItem)} (${formatQuantity(updatedItem)})`,
      );
    }
  });

  if (changes.length === 0) return "";
  const now = new Date();
  const modifiedDate = `${String(now.getMonth() + 1).padStart(2, "0")}/${String(
    now.getDate(),
  ).padStart(2, "0")}`;
  return [
    `■주문처 : ${clientName} [수정주문 (${modifiedDate})]`,
    "---------------------------------------------",
    "  구  분      Item    (Qty)",
    "---------------------------------------------",
    ...changes,
  ].join("\n");
};

const getFormattedNow = () => {
  const now = new Date();
  return `${String(now.getFullYear()).slice(2)}${String(
    now.getMonth() + 1,
  ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${String(
    now.getHours(),
  ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(
    now.getSeconds(),
  ).padStart(2, "0")}`;
};

// API 응답을 다양한 형식으로 처리하여 클라이언트 정보 추출
const extractClientDataFromResponse = (responseData) => {
  if (!responseData) return null;

  if (Array.isArray(responseData)) {
    return responseData.length > 0 ? responseData[0] : null;
  }

  if (
    typeof responseData === "object" &&
    responseData.data &&
    Array.isArray(responseData.data)
  ) {
    return responseData.data.length > 0 ? responseData.data[0] : null;
  }

  if (
    typeof responseData === "object" &&
    !Array.isArray(responseData) &&
    Object.keys(responseData).length > 0
  ) {
    return responseData;
  }

  return null;
};

const App = () => {
  const [isLandscape, setIsLandscape] = useState(true);
  const {
    userType,
    Server_IP,
    token,
    managerName,
    userId,
    setUserId,
    logout,
    showExpiryModal,
    setShowExpiryModal,
  } = useAuth();
  const [comName, setComName] = useState("M");
  const [clientQuery, setClientQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [clientInfo, setClientInfo] = useState([]);
  const [printClientInfo, setPrintClientInfo] = useState(null); // 인쇄용 거래처정보
  const [isNewOrder, setIsNewOrder] = useState(false); // 신규 주문 여부를 관리하는 상태
  const [clientItemList, setClientItemList] = useState([]);
  const [clientList, setClientList] = useState([]); // 관리자모드: 거래처 목록 (인쇄 시 조회용)
  const [orderBookList, setOrderBookList] = useState([]);
  const [orderItemList, setOrderItemList] = useState([]);
  const [orderItemListLoadedForRowId, setOrderItemListLoadedForRowId] =
    useState(null);
  const originalOrderItemsRef = useRef(null);
  const isCompletedOrderEditRef = useRef(false);
  const [activeTab, setActiveTab] = useState("companySelect"); // 기본 탭은 '회사검색'
  const currentYear = new Date().getFullYear();
  const [queryYear, setQueryYear] = useState(String(currentYear).slice(2)); // 예: "25"
  const [resetInfoCard, setResetInfoCard] = useState(false);
  const [showTable_3, setShowTable_3] = useState(false);
  const [targetTableToDelete, setTargetTableToDelete] = useState(null);
  const location = useLocation();

  const resolveComNameFromMajorTransaction = (
    majorTransaction,
    fallback = "M",
  ) => {
    const normalizedMajorTransaction = majorTransaction?.trim();
    if (!normalizedMajorTransaction) return fallback;
    if (normalizedMajorTransaction === "T") return "T";
    if (normalizedMajorTransaction === "M") return "M";
    return fallback;
  };

  const [selectedQty, setSelectedQty] = useState(null);

  const [selectedProductCode, setSelectedProductCode] = useState(null);
  const [selectedOrderBookRowId, setSelectedOrderBookRowId] = useState(null);
  const [selectedOrderItemRowOIserial, setSelectedOrderItemRowOIserial] =
    useState(null);

  const getOrderClientName = useCallback(async () => {
    const currentClientInfo = Array.isArray(clientInfo)
      ? clientInfo[0] || {}
      : clientInfo || {};
    const selectedClient = clientList.find(
      (client) => String(client?.ClientCode) === String(userId),
    );

    const selectedClientName = selectedClient?.ClientName?.trim();
    if (selectedClientName) {
      return selectedClientName;
    }

    const currentClientName = currentClientInfo.ClientName?.trim();
    if (
      currentClientName &&
      currentClientInfo.ClientCode != null &&
      String(currentClientInfo.ClientCode) === String(userId)
    ) {
      return currentClientName;
    }

    if (userId) {
      try {
        const response = await axios.post(
          `${Server_IP}/api/getClientInfo/`,
          { clientCode: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const fetchedClient = extractClientDataFromResponse(response.data);
        const fetchedClientName = fetchedClient?.ClientName?.trim();
        if (
          fetchedClientName &&
          String(fetchedClient.ClientCode) === String(userId)
        ) {
          return fetchedClientName;
        }
      } catch (error) {
        console.error("주문처 거래처명 조회 실패:", error);
      }
    } else {
      const searchedClientName = clientQuery.trim();
      if (searchedClientName) {
        return searchedClientName;
      }
    }

    toast.error(
      "거래처명을 확인할 수 없어 시놀로지 챗 메시지를 전송하지 못했습니다.",
    );
    return "";
  }, [Server_IP, token, clientInfo, clientList, clientQuery, userId]);
  const [isDeletingOrderItem, setIsDeletingOrderItem] = useState(false);

  const [slideOpen, setSlideOpen] = useState(false);
  const [requestText, setRequestText] = useState("");
  const [preRequestText, setPreRequestText] = useState(""); // 백업용
  const [orderBookEditMode, setOrderBookEditMode] = useState(false);
  const [buttonText, setButtonText] = useState("신규주문");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [confirmEmptyOrderBookDelete, setConfirmEmptyOrderBookDelete] =
    useState(false);
  const bypassEmptyOrderBookDeleteConfirmRef = useRef(false);
  const [showOrderSaveModal, setShowOrderSaveModal] = useState(false);
  const [orderSaveModalMessage, setOrderSaveModalMessage] = useState(
    "발주가 정상적으로 저장되었습니다. 감사합니다.",
  );
  const [showOutboundNoticeModal, setShowOutboundNoticeModal] = useState(false);
  const [outboundNoticeList, setOutboundNoticeList] = useState([]);
  const [dismissedOutboundNoticeSeqs, setDismissedOutboundNoticeSeqs] =
    useState([]);

  const OUTBOUND_NOTICE_HIDE_KEY_PREFIX = "outbound_notice_hide_until_";

  const getTodayKey = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [currentPage_1, setCurrentPage_1] = useState(1);
  const [currentPage_2, setCurrentPage_2] = useState(1);

  const visibleOutboundNotices = useMemo(
    () =>
      outboundNoticeList.filter(
        (notice) => !dismissedOutboundNoticeSeqs.includes(notice.seq),
      ),
    [outboundNoticeList, dismissedOutboundNoticeSeqs],
  );

  const getOutboundNoticeHideKey = (seq) =>
    `${OUTBOUND_NOTICE_HIDE_KEY_PREFIX}${seq}`;

  // useMemo로 최적화: 렌더링마다 재계산 방지
  const OrderDate = useMemo(
    () =>
      (() => {
        const now = new Date();
        return `${String(now.getFullYear()).slice(2)}${String(
          now.getMonth() + 1,
        ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
      })(),
    [],
  );
  const handleYearChange = (e) => {
    const selectedYear = e.target.value;
    setQueryYear(selectedYear.slice(2)); // 뒤 2자리만 저장
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;
      setIsLandscape(isLandscape);
    };

    window.addEventListener("orientationchange", handleOrientationChange);
    handleOrientationChange();

    return () =>
      window.removeEventListener("orientationchange", handleOrientationChange);
  }, []);
  // App.js 내부에서 currentPage_1을 강제로 바꾸는 로직을 제거했습니다.
  // 이 기능은 Table_1.jsx 컴포넌트 내부에서 자기 자신의 리스트(filteredList)를 기반으로 올바르게 수행하도록 수정되었습니다.

  useEffect(() => {
    const fetchData = async () => {
      // 보안 방어: 클라이언트 권한(userType === 'client')인데 userId가 null인 경우
      // 백엔드에서 모든 데이터가 노출되는 보안 결함을 원천 차단
      if (userType === "client" && !userId) {
        toast.error("비정상적인 접근이 감지되었습니다. 다시 로그인해 주세요.");
        logout();
        return;
      }

      // 상태 초기화
      setOrderBookList([]);
      setOrderItemList([]);
      setClientItemList([]);

      try {
        // 기존 export된 함수 사용
        await getClientInfo(Server_IP, token, userId, setClientInfo);
        await getOrderBook(
          Server_IP,
          token,
          userId,
          queryYear,
          setOrderBookList,
        );
        await getClientItemList(Server_IP, token, userId, setClientItemList);
        // 모든 데이터 로딩 후 상태 초기화
        resetSelections();
      } catch (error) {
        console.error("데이터 로딩 중 오류 발생:", error);
      }
    };

    fetchData();
  }, [Server_IP, token, userId, queryYear]);

  useEffect(() => {
    const fetchOutboundNotices = async () => {
      if (!token) {
        setOutboundNoticeList([]);
        setDismissedOutboundNoticeSeqs([]);
        setShowOutboundNoticeModal(false);
        return;
      }

      const notices = await getOutboundNoticeList(Server_IP, token);
      const todayKey = getTodayKey();
      const hiddenSeqs = notices
        .filter(
          (notice) =>
            localStorage.getItem(getOutboundNoticeHideKey(notice.seq)) ===
            todayKey,
        )
        .map((notice) => notice.seq);

      setOutboundNoticeList(notices);
      setDismissedOutboundNoticeSeqs(hiddenSeqs);
      setShowOutboundNoticeModal(
        notices.some((notice) => !hiddenSeqs.includes(notice.seq)),
      );
    };

    fetchOutboundNotices();
  }, [Server_IP, token]);

  useEffect(() => {
    const currentClientInfo = Array.isArray(clientInfo)
      ? clientInfo[0] || null
      : clientInfo || null;

    if (!currentClientInfo) {
      return;
    }

    setComName((prev) =>
      resolveComNameFromMajorTransaction(
        currentClientInfo.MajorTransaction,
        prev,
      ),
    );
  }, [clientInfo]);

  useEffect(() => {
    setOrderItemListLoadedForRowId(null);

    if (selectedOrderBookRowId && !isNewOrder) {
      let isActive = true;

      (async () => {
        const items = await showOrderItemList(
          Server_IP,
          token,
          selectedOrderBookRowId,
          setOrderItemList,
        );

        if (isActive && Array.isArray(items)) {
          setOrderItemListLoadedForRowId(selectedOrderBookRowId);
        }
      })();

      return () => {
        isActive = false;
      };
    }
  }, [selectedOrderBookRowId, isNewOrder]); // isNewOrder dependency 추가

  // 선택 상태 초기화 (모달/슬라이드 닫을 때)
  const resetSelections = () => {
    setSelectedProductCode(null);
    setSelectedOrderBookRowId(null);
    setSelectedOrderItemRowOIserial(null);
  };

  const resetInputs = () => {
    setProductQuery(""); // queryBox 초기화
  };

  const reloadOrderBook = useCallback(async () => {
    if (userType === "client" && !userId) {
      toast.error("비정상적인 접근이 감지되었습니다. 다시 로그인해 주세요.");
      logout();
      return;
    }
    return await getOrderBook(
      Server_IP,
      token,
      userId,
      queryYear,
      setOrderBookList,
    );
  }, [userType, userId, logout, Server_IP, token, queryYear]);

  const handleOrderBookAddModifyBtn = useCallback(
    async (e) => {
      // 슬라이드 OPEN
      const selectedOrder = orderBookList.find(
        (item) => item.OBserial === selectedOrderBookRowId,
      );

      const isLocked =
        selectedOrder?.OrderStatus?.includes("출고") ||
        selectedOrder?.OrderStatus?.includes("승인") ||
        selectedOrder?.OrderStatus?.includes("확인");

      if (selectedOrder && !isLocked) {
        isCompletedOrderEditRef.current =
          selectedOrder.OrderStatus === "주문완료";
        originalOrderItemsRef.current = null;
        if (isCompletedOrderEditRef.current) {
          const originalItems = await showOrderItemList(
            Server_IP,
            token,
            selectedOrderBookRowId,
            setOrderItemList,
          );
          if (!Array.isArray(originalItems)) {
            isCompletedOrderEditRef.current = false;
            toast.error("수정 전 주문 내역을 불러오지 못했습니다. 다시 시도해 주세요.");
            return;
          }
          originalOrderItemsRef.current = originalItems.map((item) => ({
            ...item,
          }));
        }
        setSlideOpen(true);
        setPreRequestText(selectedOrder.clientRequest); // 백업용
        setRequestText(selectedOrder.clientRequest);
        return;
      }

      // ✅ Race Condition 방지: OBserial을 미리 요청하지 않고,
      // addOrderBook 내부에서 트랜잭션으로 원자적 생성 후 응답값 사용
      const results = await getSupplyTypeCode(Server_IP, token, userId);
      const infoToUse = Array.isArray(clientInfo)
        ? clientInfo[0] || {}
        : clientInfo || {};

      const orderBookData = {
        // OBserial 미포함 → 서버 addOrderBook 내부에서 트랜잭션으로 안전하게 생성
        ClientCode: userId,
        Client: infoToUse.ClientName || "",
        DeputyName: infoToUse.DeputyName || "",
        Tel: infoToUse.Tel || "",
        OrderDate: OrderDate,
        ClientOrderNo: "",
        TotalAmount: 0,
        RequestDate: "",
        etc: "",
        OrderStatus: "주문중..",
        InputName: managerName != null ? managerName : userId,
        InputTime: getFormattedNow(),
        ModifyNumber: 0,
        Status: "",
        Approval: "",
        WhatCom: comName,
        UdiReport: JSON.stringify([
          {
            Sup: results?.[0]?.STC ?? "1",
            Dif: results?.[0]?.PLC ?? "0",
            Com: results?.[0]?.NAM ?? "",
            Bcn: results?.[0]?.COD ?? "",
          },
        ]),
      };
      originalOrderItemsRef.current = null;
      isCompletedOrderEditRef.current = false;
      setOrderItemList([]);
      resetInputs();

      addOrderBook(
        e,
        Server_IP,
        token,
        orderBookData,
        setOrderBookList,
        (finalizedData) => {
          // finalizedData.OBserial = 서버가 트랜잭션으로 생성한 안전한 번호
          reloadOrderBook();
          setSelectedOrderBookRowId(finalizedData.OBserial);
          setOrderBookEditMode(true);
          setButtonText("주문수정");
          setSlideOpen(true);
        },
      );
    },
    [
      orderBookList,
      selectedOrderBookRowId,
      Server_IP,
      token,
      userId,
      clientInfo,
      OrderDate,
      managerName,
      comName,
      reloadOrderBook,
      resetInputs,
      userType,
    ],
  );

  // 삭제 모달 닫기 및 상태 초기화
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTargetId(null);
    setConfirmEmptyOrderBookDelete(false);
  };

  const handleModalCancelDelete = () => {
    if (confirmEmptyOrderBookDelete) {
      bypassEmptyOrderBookDeleteConfirmRef.current = true;
      closeDeleteModal();
      handleSlideClose();
      return;
    }
    closeDeleteModal();
  };

  const onClickBookDeleteButton = (id) => {
    setConfirmEmptyOrderBookDelete(false);
    setDeleteTargetId(id); // 삭제 대상 ID 상태에 저장
    setShowDeleteModal(true); // 모달 띄우기
  };

  const onClickItemDeleteButton = (id) => {
    const selectedOrder = orderBookList.find(
      (item) => item.OBserial === selectedOrderBookRowId,
    );
    const orderStatus = selectedOrder?.OrderStatus?.trim();
    const canSendModifiedOrderMessage =
      orderStatus === "주문완료" || isOrderInProgress(orderStatus);

    if (canSendModifiedOrderMessage && !isCompletedOrderEditRef.current) {
      isCompletedOrderEditRef.current = true;
      originalOrderItemsRef.current = orderItemList.map((item) => ({
        ...item,
      }));
      setPreRequestText(selectedOrder.clientRequest);
      setRequestText(selectedOrder.clientRequest);
      setOrderBookEditMode(true);
      setButtonText("주문수정");
      setSlideOpen(true);
    }

    setDeleteTargetId(id); // 삭제 대상 ID 상태에 저장
    setShowDeleteModal(true); // 모달 띄우기
  };

  const orderBookReset = useCallback(() => {
    setRequestText("");
    resetSelections();
    setOrderBookEditMode(false);
    setIsNewOrder(true);
    setButtonText("신규주문");
    setResetInfoCard(false);
  }, []);

  const handleDeleteOnOrderBook = useCallback(
    async (id) => {
      if (!id) return;
      setSelectedOrderBookRowId(null); // 삭제전 현재행 없앰
      setOrderItemList([]);
      const orderDeleted = await deleteOrderBook(
        deleteTargetId,
        Server_IP,
        token,
        reloadOrderBook, // 삭제 후 ORDER_BOOK 재로드
        orderBookReset, // 선택해제
      );
      if (orderDeleted && confirmEmptyOrderBookDelete) {
        originalOrderItemsRef.current = null;
        isCompletedOrderEditRef.current = false;
        setSlideOpen(false);
      }
      closeDeleteModal();
    },
    [
      deleteTargetId,
      Server_IP,
      token,
      reloadOrderBook,
      orderBookReset,
      confirmEmptyOrderBookDelete,
    ],
  );

  const onOrderItemReset = useCallback(() => {
    setSelectedOrderItemRowOIserial(null);
    setSelectedProductCode(null);
    setProductQuery("");
  }, []);

  const handleDeleteOnOrderItem = useCallback(
    async (id) => {
      if (!id) return;
      setSelectedOrderItemRowOIserial(null); // 삭제전 현재 OI 없앰
      setIsDeletingOrderItem(true);
      try {
        const deleted = await deleteOrderItem(
          id,
          Server_IP,
          token,
          () =>
            showOrderItemList(
              Server_IP,
              token,
              selectedOrderBookRowId,
              setOrderItemList,
            ),
          onOrderItemReset, // 가장 마지막에 실행됨
        );
        if (deleted) {
          closeDeleteModal();
        }
      } finally {
        setIsDeletingOrderItem(false);
      }
    },
    [Server_IP, token, selectedOrderBookRowId, onOrderItemReset],
  );

  const handleConfirmDelete = useCallback(() => {
    if (targetTableToDelete === "BOOK") {
      handleDeleteOnOrderBook(deleteTargetId);
    } else {
      handleDeleteOnOrderItem(deleteTargetId);
    }
  }, [
    targetTableToDelete,
    deleteTargetId,
    handleDeleteOnOrderBook,
    handleDeleteOnOrderItem,
  ]);

  const getNextSeq = useCallback(() => {
    if (!orderItemList || orderItemList.length === 0) return 1;
    const maxSeq = Math.max(...orderItemList.map((item) => item.Seq || 0));
    return maxSeq + 1;
  }, [orderItemList]);

  const handleAddOrderItem = useCallback(
    async (item) => {
      if (!selectedOrderBookRowId) {
        toast.error("주문서가 선택되지 않았습니다.");
        return;
      }

      const existingOIserial = orderItemList.find(
        (i) => i.ProductCode === item.ProductCode,
      )?.OIserial;

      try {
        const serial = await getOIserial(
          Server_IP,
          token,
          selectedOrderBookRowId,
        );

        if (!serial) {
          toast.error("품목 시리얼 번호 생성 실패");
          return;
        }

        const orderItemData = {
          OIserial: existingOIserial ?? serial,
          OBserial: selectedOrderBookRowId,
          Seq: getNextSeq(),
          ProductCode: item.ProductCode,
          ItemName: item.AliasName,
          ModelName: item.ModelName,
          Qty: item.Qty,
          UnitPrice: item.UnitPrice,
          DiscountPrice: item.UnitPrice,
          Amount: Math.round((item.UnitPrice * item.Qty) / 1.1),
          VatAmount:
            Math.round(item.UnitPrice * item.Qty) -
            Math.round((item.UnitPrice * item.Qty) / 1.1),
          RemainQty: item.Qty,
          Remark: "",
          // DiscountPrice가 UnitPrice와 다를 때만 할인 적용 표시
          DiscountCheck: item.DiscountPrice !== item.UnitPrice ? "Y" : "",
          InputName: managerName != null ? managerName : userId,
          InputTime: getFormattedNow(),
          ModifyNumber: 0,
          Status: "F",
          Approval: "",
          SuplyFlag: "1", // SuplyFlag 1자리=공급구분코드(1.출고,2.반품,3.폐기,4.일반반품,5.회수반품,6.임대반품), 2자리=내부세부코드(1.주문출고,교환출고)
          Used: "",
        };

        if (existingOIserial) {
          const updatedItemList = orderItemList.map((orderItem) =>
            orderItem.ProductCode === item.ProductCode
              ? { ...orderItem, Qty: item.Qty }
              : orderItem,
          );

          setOrderItemList(updatedItemList);

          updateOrderItem(
            Server_IP,
            token,
            orderItemData,
            setOrderItemList,
            () => {
              showOrderItemList(
                Server_IP,
                token,
                selectedOrderBookRowId,
                setOrderItemList,
              );
            },
          );
        } else {
          addOrderItem(
            Server_IP,
            token,
            orderItemData,
            setOrderItemList,
            () => {
              showOrderItemList(
                Server_IP,
                token,
                selectedOrderBookRowId,
                setOrderItemList,
              );
            },
          );
        }
      } catch (error) {
        console.error("시리얼 번호 생성 중 오류:", error);
        toast.error("서버 오류로 시리얼 번호를 가져올 수 없습니다.");
      }
    },
    [
      selectedOrderBookRowId,
      orderItemList,
      Server_IP,
      token,
      managerName,
      userId,
      getNextSeq,
    ],
  );

  const handleRequestBoxChange = (e) => {
    setRequestText(e.target.value);
  };

  const handleReloadOrderBook = useCallback(async () => {
    await reloadOrderBook();
    setSelectedOrderBookRowId(null);
    setSelectedOrderItemRowOIserial(null);
    setSelectedProductCode(null);
    setOrderItemList([]);
    setOrderItemListLoadedForRowId(null);
    setOrderBookEditMode(false);
    setButtonText("신규주문");
  }, [reloadOrderBook]);

  const handleReloadOrderList = useCallback(async () => {
    // selectedOrderBookRowId가 있을 때 직접 showOrderItemList 호출
    if (selectedOrderBookRowId) {
      await showOrderItemList(
        Server_IP,
        token,
        selectedOrderBookRowId,
        setOrderItemList,
      );
    }
  }, [selectedOrderBookRowId, Server_IP, token]);

  const handleSlideClose = useCallback(async () => {
    if (!orderItemList?.length) {
      if (!selectedOrderBookRowId) {
        toast.error("삭제할 주문번호를 찾을 수 없습니다.");
        return;
      }

      const originalItems = originalOrderItemsRef.current;
      if (
        isCompletedOrderEditRef.current &&
        originalItems?.length &&
        !bypassEmptyOrderBookDeleteConfirmRef.current
      ) {
        setDeleteTargetId(selectedOrderBookRowId);
        setTargetTableToDelete("BOOK");
        setConfirmEmptyOrderBookDelete(true);
        setShowDeleteModal(true);
        return;
      }

      if (isCompletedOrderEditRef.current && originalItems?.length) {
        bypassEmptyOrderBookDeleteConfirmRef.current = false;
        const orderSaved = await updateFieldTable(
          Server_IP,
          token,
          "ORDER_BOOK",
          "OBserial",
          selectedOrderBookRowId,
          "OrderStatus",
          "주문완료",
        );
        if (!orderSaved) return;

        const clientName = await getOrderClientName();
        if (clientName) {
          const message = createModifiedOrderMessage(
            clientName,
            originalItems,
            [],
          );
          if (message) {
            await sendChatMessage(Server_IP, token, message);
          }
        }

        originalOrderItemsRef.current = null;
        isCompletedOrderEditRef.current = false;
        await reloadOrderBook();
        setSlideOpen(false);
        setSelectedProductCode(null);
        setSelectedOrderItemRowOIserial(null);
        resetInputs();
        setOrderSaveModalMessage("발주가 정상적으로 저장되었습니다. 감사합니다.");
        setShowOrderSaveModal(true);
        return;
      }

      const orderDeleted = await deleteOrderBook(
        selectedOrderBookRowId,
        Server_IP,
        token,
        reloadOrderBook,
        orderBookReset,
      );
      if (!orderDeleted) return;

      setOrderItemList([]);
      originalOrderItemsRef.current = null;
      isCompletedOrderEditRef.current = false;
      setSlideOpen(false);
      setSelectedProductCode(null);
      setSelectedOrderItemRowOIserial(null);
      resetInputs();
      return;
    }

    const nextOrderSaveMessage =
      "발주가 정상적으로 저장되었습니다. 감사합니다.";

    let needsReload = false;
    let saveSucceeded = true;
    if ((requestText || "").trim() !== (preRequestText || "").trim()) {
      const requestSaved = await updateClientRequest(
        Server_IP,
        token,
        requestText,
        selectedOrderBookRowId,
      );
      saveSucceeded = requestSaved && saveSucceeded;
      needsReload = requestSaved || needsReload;
    }

    if (orderItemList && orderItemList.length > 0) {
      const orderSaved = await updateFieldTable(
        Server_IP,
        token,
        "ORDER_BOOK",
        "OBserial",
        selectedOrderBookRowId,
        "OrderStatus",
        "주문완료",
      );
      saveSucceeded = orderSaved && saveSucceeded;
      needsReload = orderSaved || needsReload;
    }

    if (
      saveSucceeded &&
      (userType === "client" || userType === "manager") &&
      orderItemList?.length > 0
    ) {
      const clientName = await getOrderClientName();
      if (clientName) {
        if (isCompletedOrderEditRef.current) {
          const originalItems = originalOrderItemsRef.current;
          if (originalItems) {
            const message = createModifiedOrderMessage(
              clientName,
              originalItems,
              orderItemList,
            );
            if (message) await sendChatMessage(Server_IP, token, message);
          }
        } else {
          await sendChatMessage(
            Server_IP,
            token,
            createOrderMessage(clientName, orderItemList),
          );
        }
      }
    }

    originalOrderItemsRef.current = null;
    isCompletedOrderEditRef.current = false;
    if (needsReload) {
      await reloadOrderBook();
    }
    setSlideOpen(false);
    setSelectedProductCode(null);
    setSelectedOrderItemRowOIserial(null);
    resetInputs();
    setOrderSaveModalMessage(nextOrderSaveMessage);
    setShowOrderSaveModal(true);
  }, [
    requestText,
    preRequestText,
    Server_IP,
    token,
    selectedOrderBookRowId,
    reloadOrderBook,
    orderBookReset,
    resetInputs,
    orderItemList,
    userType,
    clientInfo,
    clientQuery,
    userId,
    getOrderClientName,
    sendChatMessage,
  ]);

  const onOrderConfirm = useCallback(
    async (OBserial) => {
      if (!OBserial) return;

      try {
        await updateFieldTable(
          Server_IP,
          token,
          "ORDER_BOOK",
          "OBserial",
          OBserial,
          "OrderStatus",
          "주문확인",
        );
        // 업데이트된 데이터 다시 fetch
        const updatedRow = await fetchUpdatedRow(
          Server_IP,
          token,
          "ORDER_BOOK",
          "OBserial",
          OBserial,
        );

        // 기존 목록에서 해당 row만 교체
        setOrderBookList((prevList) =>
          prevList.map((row) =>
            row.OBserial === updatedRow.OBserial ? updatedRow : row,
          ),
        );
      } catch (err) {
        console.error("주문확인 실패:", err);
      }
    },
    [Server_IP, token],
  );

  const onConfirmCancel = useCallback(
    async (OBserial) => {
      if (!OBserial) return;

      try {
        await updateFieldTable(
          Server_IP,
          token,
          "ORDER_BOOK",
          "OBserial",
          OBserial,
          "OrderStatus",
          "주문완료",
        );
        // 업데이트된 데이터 다시 fetch
        const updatedRow = await fetchUpdatedRow(
          Server_IP,
          token,
          "ORDER_BOOK",
          "OBserial",
          OBserial,
        );

        // 기존 목록에서 해당 row만 교체
        setOrderBookList((prevList) =>
          prevList.map((row) =>
            row.OBserial === updatedRow.OBserial ? updatedRow : row,
          ),
        );
      } catch (err) {
        console.error("주문확인 삭제 실패:", err);
      }
    },
    [Server_IP, token],
  );

  const yearOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i <= 2; i++) {
      const year = currentYear - i;
      options.push(
        <option key={year} value={year}>
          {year}
        </option>,
      );
    }
    return options;
  }, [currentYear]);

  const handleHamburgerClick = () => {
    if (userType === "client") {
      logout();
    } else {
      setUserId(null);
      setResetInfoCard(true); // trigger 발생
      orderBookReset(); // 관리자가 업체 전환 시 상태 초기화
    }
  };

  const closeOrderSaveModal = () => {
    setShowOrderSaveModal(false);
  };

  const closeOutboundNoticeModal = (seq) => {
    if (seq === undefined || seq === null) {
      return;
    }

    setDismissedOutboundNoticeSeqs((prev) =>
      prev.includes(seq) ? prev : [...prev, seq],
    );
  };

  const hideOutboundNoticeToday = (seq) => {
    if (seq === undefined || seq === null) {
      return;
    }

    localStorage.setItem(getOutboundNoticeHideKey(seq), getTodayKey());
    setDismissedOutboundNoticeSeqs((prev) =>
      prev.includes(seq) ? prev : [...prev, seq],
    );

    const remainingNotices = visibleOutboundNotices.filter(
      (notice) => notice.seq !== seq,
    );
    setShowOutboundNoticeModal(remainingNotices.length > 0);
  };

  const [printTriggerId, setPrintTriggerId] = useState(null);
  const printTimeoutRef = useRef(null);
  const lastPrintIdRef = useRef(null);

  // 고유한 ID로 인쇄 트리거하여 중복 방지
  useEffect(() => {
    if (printTriggerId !== null && printTriggerId !== lastPrintIdRef.current) {
      lastPrintIdRef.current = printTriggerId;

      // 이전 타임아웃 취소
      if (printTimeoutRef.current) {
        clearTimeout(printTimeoutRef.current);
      }

      // DOM 렌더링 완료를 기다림 (상태 업데이트 반영 시간 확보)
      printTimeoutRef.current = setTimeout(() => {
        window.print();
        printTimeoutRef.current = null;
      }, 150);
    }

    // 클린업
    return () => {
      if (printTimeoutRef.current) {
        clearTimeout(printTimeoutRef.current);
        printTimeoutRef.current = null;
      }
    };
  }, [printTriggerId]);

  const handlePrintOrder = async () => {
    // 1. 거래처 정보 결정 및 상태 업데이트

    // 클라이언트 모드: clientInfo가 이미 있는 경우
    if (
      clientInfo &&
      (Array.isArray(clientInfo)
        ? clientInfo.length > 0
        : Object.keys(clientInfo).length > 0)
    ) {
      const infoToPrint = Array.isArray(clientInfo)
        ? clientInfo[0]
        : clientInfo;
      setPrintClientInfo(infoToPrint);
      setPrintTriggerId(Date.now()); // useEffect에서 인쇄 트리거
      return;
    }

    // 관리자모드: 거래처 정보가 없어 조회해야 하는 경우
    if (!userId && selectedOrderBookRowId && clientList.length > 0) {
      const selectedOrder = orderBookList.find(
        (item) => item.OBserial === selectedOrderBookRowId,
      );

      if (selectedOrder?.Client) {
        const matchingClient = clientList.find(
          (c) => c.ClientName === selectedOrder.Client,
        );

        if (matchingClient?.ClientCode) {
          try {
            const response = await axios.post(
              `${Server_IP}/api/getClientInfo/`,
              { clientCode: matchingClient.ClientCode },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            const clientData = extractClientDataFromResponse(response.data);
            if (clientData) {
              setPrintClientInfo(clientData);
              setPrintTriggerId(Date.now());
              return;
            }
          } catch (error) {
            console.error("거래처 정보 조회 실패:", error);
          }
        }
      }
    }

    // 기본: 특별한 정보 로드 없이 현재 상태로 인쇄
    setPrintTriggerId(Date.now());
  };

  const selectedOrderBook = useMemo(
    () =>
      orderBookList.find((item) => item.OBserial === selectedOrderBookRowId),
    [orderBookList, selectedOrderBookRowId],
  );

  return (
    <>
      <OrderPrint
        selectedOrderBook={selectedOrderBook}
        orderItemList={orderItemList}
        clientInfo={clientInfo}
        printClientInfo={printClientInfo}
      />
      <div className="no-print">
        {!isLandscape && (
          <div className="orientation-warning">
            이 앱은 가로모드에서 최적화되어 있습니다. 화면을 돌려주세요.
          </div>
        )}
        <MainRoutes />

        {/* fixed header: hide on manual pages to avoid duplicate headers */}
        {!location.pathname.startsWith("/manual") && (
          <div className="fixed-header">
            <Header />
          </div>
        )}
        <div className="content-below-header d-flex">
          <div className="left-container " style={{ width: "300px" }}>
            <InfoCard
              Server_IP={Server_IP}
              token={token}
              userType={userType}
              clientCode={userId}
              getClientInfo={getClientInfo}
              resetInfoCard={resetInfoCard}
            />
            {/* 직원으로 로그인하였을 때만 보이기 */}
            {userType !== "client" && (
              <>
                <div className="no-gap-tabs">
                  <Tabs
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    clientInfo={clientInfo}
                  />

                  {activeTab === "companySelect" && (
                    <InfoCardStaffOnly
                      Server_IP={Server_IP}
                      token={token}
                      comName={comName}
                      setComName={setComName}
                      setUserId={setUserId}
                      orderBookReset={orderBookReset}
                      setResetInfoCard={setResetInfoCard}
                      clientQuery={clientQuery}
                      setClientQuery={setClientQuery}
                      setClientListFromParent={setClientList}
                    />
                  )}

                  {activeTab === "passwordInit" && (
                    <PassWordInitialize
                      Server_IP={Server_IP}
                      token={token}
                      userId={userId}
                      userEmail={
                        Array.isArray(clientInfo)
                          ? clientInfo[0]?.Email || clientInfo[0]?.email
                          : clientInfo?.Email || clientInfo?.email
                      }
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <div className="right-container flex-grow-1 position-relative">
            <div className="d-flex table-row-container gap-4">
              <div
                className="right-1-container order-history-container"
                style={{
                  visibility: slideOpen ? "hidden" : "visible",
                }}
              >
                <div className="table-wrapper">
                  <div className="section-title bg-primary text-white d-flex justify-content-between align-items-center px-3">
                    <div className="d-flex align-items-center">
                      {userType !== "client" && (
                        <button
                          className="btn btn-sm btn-no-outline-light me-2"
                          onClick={handleHamburgerClick}
                        >
                          <FaBars className="hamburger-icon" size={20} />
                        </button>
                      )}
                      <h5 className="mb-0">
                        {!resetInfoCard ? "주문이력" : "주문목록"}
                      </h5>
                    </div>
                    <div className="d-flex align-items-center">
                      <select
                        className="form-select form-select-sm me-2"
                        style={{ width: "auto" }}
                        defaultValue={currentYear}
                        onChange={handleYearChange}
                      >
                        {yearOptions}
                      </select>
                      <button
                        className="btn btn-sm btn-outline-light"
                        onClick={handleReloadOrderBook}
                      >
                        새로고침
                      </button>
                    </div>
                  </div>

                  <div className="section-content">
                    <Table_0
                      userType={userType}
                      onOrderConfirm={onOrderConfirm}
                      onConfirmCancel={onConfirmCancel}
                      userId={userId}
                      setIsNewOrder={setIsNewOrder}
                      handleOrderBookAddModifyBtn={handleOrderBookAddModifyBtn}
                      onClickBookDeleteButton={onClickBookDeleteButton}
                      list={orderBookList}
                      orderItemList={orderItemList}
                      resetInputs={resetInputs}
                      setOrderItemList={setOrderItemList}
                      orderItemListLoadedForRowId={orderItemListLoadedForRowId}
                      selectedOrderBookRowId={selectedOrderBookRowId}
                      setSelectedOrderItemRowOIserial={
                        setSelectedOrderItemRowOIserial
                      }
                      setSelectedOrderBookRowId={setSelectedOrderBookRowId}
                      handleDeleteOnOrderBook={handleDeleteOnOrderBook}
                      setSelectedProductCode={setSelectedProductCode}
                      orderBookReset={orderBookReset}
                      orderBookEditMode={orderBookEditMode}
                      setOrderBookEditMode={setOrderBookEditMode}
                      setButtonText={setButtonText}
                      buttonText={buttonText}
                      setRequestText={setRequestText}
                      setPreRequestText={setPreRequestText}
                      setShowDeleteModal={setShowDeleteModal}
                      setShowTable_3={setShowTable_3}
                      setTargetTableToDelete={setTargetTableToDelete}
                      clientInfo={clientInfo}
                      setClientQuery={setClientQuery}
                      clientQuery={clientQuery}
                    />
                  </div>
                </div>
              </div>
              <div className="right-2-container flex-fill order-items-container">
                <div className="table-wrapper">
                  <div className="section-title bg-success text-white d-flex justify-content-between align-items-center px-3">
                    <h5 className="mb-0">주문품목</h5>
                    <div>
                      <button
                        className="btn btn-sm btn-outline-light me-2"
                        onClick={handlePrintOrder}
                        disabled={
                          !selectedOrderBookRowId || orderItemList.length === 0
                        }
                      >
                        주문서 인쇄
                      </button>
                      <button
                        className="btn btn-sm btn-outline-light"
                        onClick={handleReloadOrderList}
                      >
                        새로고침
                      </button>
                    </div>
                  </div>
                  <div className="section-content">
                    <Table_2
                      userType={userType}
                      userId={userId}
                      orderBookEditMode={orderBookEditMode}
                      setOrderBookEditMode={setOrderBookEditMode}
                      setProductQuery={setProductQuery}
                      clientItemList={clientItemList}
                      productQuery={productQuery}
                      orderItemList={orderItemList}
                      selectedProductCode={selectedProductCode}
                      setSelectedProductCode={setSelectedProductCode}
                      currentPage={currentPage_2}
                      setCurrentPage={setCurrentPage_2}
                      onClickItemDeleteButton={onClickItemDeleteButton}
                      setSelectedOrderItemRowOIserial={
                        setSelectedOrderItemRowOIserial
                      }
                      onOrderItemReset={onOrderItemReset}
                      setShowTable_3={setShowTable_3}
                      setSelectedQty={setSelectedQty}
                      setTargetTableToDelete={setTargetTableToDelete}
                      setShowDeleteModal={setShowDeleteModal}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={`slide-panel ${slideOpen ? "open" : ""}`}>
              <div className="section-title bg-info text-white d-flex justify-content-between align-items-center px-3">
                <h5 className="mb-0">주문서 작성</h5>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={handleSlideClose}
                  disabled={isDeletingOrderItem}
                >
                  {orderItemList?.length > 0 ||
                  (isCompletedOrderEditRef.current &&
                    originalOrderItemsRef.current?.length > 0)
                    ? "발주완료"
                    : "창 닫기"}
                </button>
              </div>
              <div className="section-content">
                <Table_1
                  slideOpen={slideOpen}
                  list={clientItemList}
                  orderItemList={orderItemList}
                  selectedProductCode={selectedProductCode}
                  setSelectedProductCode={setSelectedProductCode}
                  setCurrentPage={setCurrentPage_1}
                  currentPage={currentPage_1}
                  requestText={requestText}
                  setRequestText={setRequestText}
                  setProductQuery={setProductQuery}
                  productQuery={productQuery}
                  handleAddOrderItem={handleAddOrderItem}
                  onRequestBoxChange={handleRequestBoxChange}
                  onRowClick={(data) => setSelectedProductCode(data.id)}
                  handleSlideClose={handleSlideClose}
                  currentPageTable_1={currentPage_1}
                  setCurrentPageTable_1={setCurrentPage_1}
                />
              </div>
            </div>
          </div>
        </div>
        {/*  모달 창들 */}
        {showExpiryModal && token && (
          <SessionExpiryModal show={showExpiryModal} onConfirm={logout} />
        )}

        {showDeleteModal && (
          <CustomModal
            show={showDeleteModal}
            onHide={handleModalCancelDelete} // 취소 또는 모달 닫기 시 실행
            onConfirm={handleConfirmDelete}
            message={
              confirmEmptyOrderBookDelete
                ? "주문품목이 모두 삭제되었습니다. 주문이력 행도 삭제하시겠습니까?"
                : "정말로 삭제하시겠습니까?"
            }
            width={420}
            height={150}
          />
        )}

        {showOrderSaveModal && (
          <CustomModal
            show={showOrderSaveModal}
            onHide={closeOrderSaveModal}
            onConfirm={closeOrderSaveModal}
            message={orderSaveModalMessage}
            confirmText="확인"
            cancelText=""
            width={420}
            height={150}
          />
        )}

        {showOutboundNoticeModal &&
          visibleOutboundNotices.length > 0 &&
          visibleOutboundNotices.map((notice, index) => {
            return (
              <OutboundNoticeModal
                key={notice.seq}
                show={showOutboundNoticeModal}
                notice={notice}
                noticeIndex={index}
                onHide={() => closeOutboundNoticeModal(notice.seq)}
                onHideToday={() => hideOutboundNoticeToday(notice.seq)}
              />
            );
          })}

        {showTable_3 && (
          <Table_3
            ProductCode={selectedProductCode}
            OBserial={selectedOrderBookRowId}
            orderQty={selectedQty}
            setShowTable_3={setShowTable_3}
            showTable_3={showTable_3}
          />
        )}
      </div>
    </>
  );
};

export default App;
